const maximumFrameHeight = 1200
const minimumFrameHeight = 48

const inlineScriptValue = (value: string) =>
	JSON.stringify(value)
		.replaceAll("<", "\\u003c")
		.replaceAll("\u2028", "\\u2028")
		.replaceAll("\u2029", "\\u2029")

const frameBootstrap = (channel: string, javascript: string) => `
(() => {
	const channel = ${JSON.stringify(channel)}
	const bundle = ${inlineScriptValue(javascript)}
	const send = (kind, payload = {}) => parent.postMessage({ channel, kind, ...payload }, "*")
	const errorText = (value) => String(value?.message ?? value ?? "React node failed").slice(0, 2000)
	addEventListener("error", (event) => send("error", { message: errorText(event.error ?? event.message) }))
	addEventListener("unhandledrejection", (event) => send("error", { message: errorText(event.reason) }))
	addEventListener("message", (event) => {
		if (event.source !== parent || event.data?.channel !== channel || event.data?.kind !== "render") return
		try {
			globalThis.__OBLIQUE_REACT_NODE_RENDER__(event.data.props ?? {})
		} catch (error) {
			send("error", { message: errorText(error) })
		}
	})
	new ResizeObserver(() => send("resize", { height: document.documentElement.scrollHeight })).observe(document.documentElement)
	const script = document.createElement("script")
	const scriptURL = URL.createObjectURL(new Blob([bundle], { type: "text/javascript" }))
	script.src = scriptURL
	script.onload = () => {
		URL.revokeObjectURL(scriptURL)
		send("ready")
	}
	script.onerror = () => {
		URL.revokeObjectURL(scriptURL)
		send("error", { message: "React node bundle could not be loaded" })
	}
	document.head.append(script)
})()
`

export const reactNodeSandboxDocument = (
	channel: string,
	javascript: string,
	nonce: string,
) => `<!doctype html>
<html>
<head>
	<meta charset="utf-8">
	<meta http-equiv="Content-Security-Policy" content="default-src 'none'; script-src 'nonce-${nonce}' blob:; style-src 'unsafe-inline'; img-src data: blob:; font-src data:; media-src data: blob:; connect-src 'none'; worker-src blob:; base-uri 'none'; form-action 'none'">
	<style>html,body,#root{margin:0;min-height:${minimumFrameHeight}px;overflow:hidden}*{box-sizing:border-box}</style>
</head>
<body>
	<div id="root"></div>
	<script nonce="${nonce}">${frameBootstrap(channel, javascript)}</script>
</body>
</html>`

export const reactNodeFrameHeight = (value: unknown) => {
	if (typeof value !== "number" || !Number.isFinite(value)) {
		return minimumFrameHeight
	}
	return Math.min(maximumFrameHeight, Math.max(minimumFrameHeight, value))
}

export const isReactNodeFrameMessage = (
	value: unknown,
	channel: string,
): value is { channel: string; kind: "ready" | "resize" | "error" } & Record<
	string,
	unknown
> => {
	if (!value || typeof value !== "object") return false
	const message = value as { channel?: unknown; kind?: unknown }
	return (
		message.channel === channel &&
		(message.kind === "ready" ||
			message.kind === "resize" ||
			message.kind === "error")
	)
}
